import React, { useRef, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Upload, X, ImageIcon } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const photoUrl = (path) => (path ? `${BACKEND}/api/uploads/${path}` : null);

/**
 * Top-photo uploader. As of v0.5.0 this also AUTO-PERSISTS the new
 * photo_path to the animal record immediately after a successful upload
 * (and on Remove). Previously the new path was only kept in local React
 * state until the user remembered to click the global Save button, which
 * caused the "photo disappears on refresh" bug.
 *
 * Props:
 *   photoPath  — current path to display
 *   onChange   — local-state setter the parent uses to re-render
 *   animalId   — when provided, the new/cleared path is PUT to
 *                `/api/animals/{animalId}` immediately. Omit to keep the
 *                old "stage in form state, save with parent" behaviour
 *                (e.g. on the "new animal" dialog where there is no id yet).
 */
export const PhotoUploader = ({ photoPath, onChange, animalId = null }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  // Persist photo_path immediately to avoid the "lost on refresh" bug.
  const persistPhotoPath = async (path) => {
    if (!animalId) return; // No id yet (e.g. inside Create dialog) — defer to parent's Save.
    try {
      await axios.put(`${API}/animals/${animalId}`, { photo_path: path });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      const msg = typeof detail === "string" ? detail : (err?.message || "Could not save");
      toast.error(status ? `Save failed (${status}): ${msg}` : `Save failed: ${msg}`);
      throw err;
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(`${API}/uploads/photo`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const newPath = res.data.path;
      // Persist to DB FIRST so a refresh always finds the photo. Only flip
      // local state once the PUT succeeds.
      await persistPhotoPath(newPath);
      onChange(newPath);
      toast.success("Photo uploaded");
    } catch (err) {
      if (err?.response) {
        const detail = err?.response?.data?.detail;
        const status = err?.response?.status;
        const msg = typeof detail === "string" ? detail : (err?.message || "Upload failed");
        toast.error(status ? `Upload failed (${status}): ${msg}` : `Upload failed: ${msg}`);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    setBusy(true);
    try {
      await persistPhotoPath("");
      onChange("");
      toast.success("Photo removed");
    } catch {
      // toast already emitted by persistPhotoPath
    } finally {
      setBusy(false);
    }
  };

  const url = photoUrl(photoPath);

  return (
    <div className="flex flex-col items-start gap-2" data-testid="photo-uploader">
      <div className="w-20 h-20 rounded-md border border-[var(--app-border)] bg-[var(--app-bg)] overflow-hidden flex items-center justify-center shrink-0">
        {url ? (
          <img
            src={url}
            alt="Ancestor"
            className="w-full h-full object-cover"
            data-testid="ancestor-photo"
          />
        ) : (
          <ImageIcon className="w-7 h-7 text-[var(--app-text-soft)]" strokeWidth={1.4} />
        )}
      </div>
      <div className="flex flex-col gap-1.5 w-20">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={handleFile}
          className="hidden"
          data-testid="photo-input"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid="btn-upload-photo"
          className="justify-center px-2 text-[11px]"
        >
          <Upload className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} />
          {busy ? "…" : photoPath ? "Replace" : "Upload"}
        </Button>
        {photoPath && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleRemove}
            disabled={busy}
            data-testid="btn-remove-photo"
            className="justify-center px-2 text-[11px] text-[var(--app-text-soft)]"
          >
            <X className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} />
            Remove
          </Button>
        )}
      </div>
    </div>
  );
};

export default PhotoUploader;
